import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { applyTheme, resolveInitialTheme, STORAGE_KEY, type Theme } from '../lib/theme';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// Mirrors the try/catch in index.html's pre-paint script: a `localStorage`
// throw (private-browsing / locked-down webview storage restrictions — a
// named user persona) must degrade to a default, never propagate. Unlike
// the script, this runs inside useState's initializer for ThemeProvider,
// which wraps every route in main.tsx with no error boundary anywhere — an
// uncaught throw here white-screens the whole app on first render.
function readStoredTheme(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Storage unavailable or full — the theme still applies for this
    // session via applyTheme(), it just won't persist across reloads.
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() =>
    resolveInitialTheme(
      readStoredTheme(),
      window.matchMedia('(prefers-color-scheme: dark)').matches,
    ),
  );

  useEffect(() => {
    applyTheme(theme);
    writeStoredTheme(theme);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggle = useCallback(() => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')), []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
